import { useEffect, useState } from 'react';
import httpClient from '../httpClient';

export interface NotificationPreferences {
    preferred_language: string;
    preferred_channels: string[];
    email_notifications: boolean;
    sms_notifications: boolean;
    push_notifications: boolean;
    in_app_notifications: boolean;
}

export interface NotificationChannel {
    id: string;
    name: string;
    description: string;
    icon: string;
}

export interface Language {
    code: string;
    name: string;
    native_name: string;
}

export const useNotificationPreferences = () => {
    const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
    const [channels, setChannels] = useState<NotificationChannel[]>([]);
    const [languages, setLanguages] = useState<Language[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [updating, setUpdating] = useState(false);

    // Fetch preferences
    const fetchPreferences = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await httpClient.get('/trader/notifications/preferences');
            setPreferences(response.data);
        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to fetch preferences');
        } finally {
            setLoading(false);
        }
    };

    // Fetch available channels
    const fetchChannels = async () => {
        try {
            const response = await httpClient.get('/trader/notifications/channels');
            setChannels(response.data.channels);
        } catch (err: any) {
            console.error('Failed to fetch channels:', err);
        }
    };

    // Fetch available languages
    const fetchLanguages = async () => {
        try {
            const response = await httpClient.get('/trader/notifications/languages');
            setLanguages(response.data.languages);
        } catch (err: any) {
            console.error('Failed to fetch languages:', err);
        }
    };

    // Update preferences
    const updatePreferences = async (newPreferences: Partial<NotificationPreferences>) => {
        try {
            setUpdating(true);
            setError(null);
            const response = await httpClient.put(
                '/trader/notifications/preferences',
                newPreferences
            );
            setPreferences(response.data.preferences);
            return response.data;
        } catch (err: any) {
            const errorMessage = err.response?.data?.error || 'Failed to update preferences';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setUpdating(false);
        }
    };

    // Test notification
    const testNotification = async (channel: string) => {
        try {
            const response = await httpClient.post('/trader/notifications/test', { channel });
            return response.data;
        } catch (err: any) {
            const errorMessage = err.response?.data?.error || 'Failed to send test notification';
            throw new Error(errorMessage);
        }
    };

    // Initialize data
    useEffect(() => {
        const initialize = async () => {
            await Promise.all([fetchPreferences(), fetchChannels(), fetchLanguages()]);
        };
        initialize();
    }, []);

    return {
        preferences,
        channels,
        languages,
        loading,
        error,
        updating,
        fetchPreferences,
        updatePreferences,
        testNotification,
        clearError: () => setError(null),
    };
};
